import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardApi } from '../api/resources';
import type { DashboardData } from '../api/types';
import { useAuth } from '../context/AuthContext';
import { formatCOP, formatDateTime, formatQty } from '../utils/format';
import { ImageLightbox } from '../components/ImageLightbox';

export function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recipePage, setRecipePage] = useState(0);
  const [lightbox, setLightbox] = useState<{ imageUrl: string; title: string } | null>(null);

  useEffect(() => {
    DashboardApi.get()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-block">Cargando panel...</div>;
  if (!data) return <div className="empty-state">No fue posible cargar la informacion del panel.</div>;

  const recipesPerPage = 9;
  const recipePageCount = Math.ceil(data.activeRecipes.length / recipesPerPage);
  const visibleRecipes = data.activeRecipes.slice(recipePage * recipesPerPage, (recipePage + 1) * recipesPerPage);

  return (
    <div>
      <h1>Hola, {user?.fullName?.split(' ')[0]}</h1>
      <p className="text-muted">Resumen operativo de la floristeria.</p>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-value">{data.supplyCount}</div>
          <div className="stat-label">Insumos activos</div>
        </div>
        <div className={`stat-card ${data.lowStockCount > 0 ? 'warning' : ''}`}>
          <div className="stat-value">{data.lowStockCount}</div>
          <div className="stat-label">Insumos con stock bajo</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.recipeCount}</div>
          <div className="stat-label">Arreglos activos</div>
        </div>
        {user?.role === 'ADMIN' && (
          <>
            <div className="stat-card">
              <div className="stat-value">{data.activeUserCount ?? '-'}</div>
              <div className="stat-label">Usuarios activos</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{data.quoteCount ?? '-'}</div>
              <div className="stat-label">Cotizaciones generadas</div>
            </div>
          </>
        )}
      </div>

      <div className="card recipe-showcase">
        <div className="card-header">
          <div>
            <h2>Arreglos destacados</h2>
            <p className="text-muted">Consulta los arreglos activos y su costo final de producción.</p>
          </div>
          <Link to="/recetas" className="btn-link">Ver todos los arreglos</Link>
        </div>
        {data.activeRecipes.length === 0 ? (
          <p className="empty-state">No hay arreglos activos para mostrar.</p>
        ) : (
          <>
            <div className="recipe-showcase-grid">
              {visibleRecipes.map((recipe) => (
                <article className="recipe-showcase-item" key={recipe.id}>
                  {recipe.imageUrl ? (
                    <button
                      type="button"
                      className="recipe-showcase-image-button"
                      onClick={() => setLightbox({ imageUrl: recipe.imageUrl!, title: recipe.name })}
                      aria-label={`Ampliar imagen de ${recipe.name}`}
                    >
                      <img src={recipe.imageUrl} alt={`Imagen de ${recipe.name}`} />
                    </button>
                  ) : (
                    <div className="recipe-showcase-placeholder" aria-label="Arreglo sin imagen">FLOWERSOFT</div>
                  )}
                  <div className="recipe-showcase-copy">
                    <Link to={`/recetas/${recipe.id}`}>{recipe.name}</Link>
                    <strong>{formatCOP(recipe.productionCost)}</strong>
                    <span>Costo final de producción</span>
                  </div>
                </article>
              ))}
            </div>
            {recipePageCount > 1 && (
              <div className="showcase-pagination" aria-label="Paginación de arreglos">
                <button type="button" className="btn btn-secondary btn-sm" disabled={recipePage === 0} onClick={() => setRecipePage((page) => page - 1)}>
                  Anteriores
                </button>
                <span>Página {recipePage + 1} de {recipePageCount}</span>
                <button type="button" className="btn btn-secondary btn-sm" disabled={recipePage === recipePageCount - 1} onClick={() => setRecipePage((page) => page + 1)}>
                  Siguientes
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {lightbox && <ImageLightbox imageUrl={lightbox.imageUrl} title={lightbox.title} onClose={() => setLightbox(null)} />}

      {data.lowStockCount > 0 && (
        <div className="card">
          <div className="card-header">
            <h2>Insumos con stock bajo</h2>
            <Link to="/inventario" className="btn-link">
              Ver inventario completo
            </Link>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Insumo</th>
                  <th className="text-right">Stock actual</th>
                  <th className="text-right">Stock minimo</th>
                </tr>
              </thead>
              <tbody>
                {data.lowStockSupplies.map((s) => (
                  <tr key={s.id}>
                    <td>{s.code}</td>
                    <td>{s.name}</td>
                    <td className="text-right text-danger">
                      {formatQty(s.currentStock)} {s.unit.name}
                    </td>
                    <td className="text-right">
                      {formatQty(s.minStock)} {s.unit.name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {user?.role === 'ADMIN' && (
      <div className="card">
        <div className="card-header">
          <h2>Ultimos movimientos de inventario</h2>
          <Link to="/inventario" className="btn-link">
            Ver inventario
          </Link>
        </div>
        {data.recentMovements.length === 0 ? (
          <p className="empty-state">Aun no se han registrado movimientos.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Insumo</th>
                  <th>Tipo</th>
                  <th className="text-right">Cantidad</th>
                  <th>Usuario</th>
                </tr>
              </thead>
              <tbody>
                {data.recentMovements.map((m) => (
                  <tr key={m.id}>
                    <td>{formatDateTime(m.createdAt)}</td>
                    <td>{m.supply?.name}</td>
                    <td>
                      <span className={`pill pill-${m.type.toLowerCase()}`}>{m.type}</span>
                    </td>
                    <td className="text-right">{formatQty(m.quantity)}</td>
                    <td>{m.user?.fullName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
